/**
 * Tenant (Organization) Manager Component
 *
 * Simplified tenant/organization creation and management.
 * Organizations are containers for projects - keep this simple.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  CheckCircle as ActiveIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface Tenant {
  id: string;
  tenant_id: string;
  organization_name: string;
  admin_email: string;
  billing_email?: string;
  status: string;
  created_at: string;
  project_count?: number;
  user_count?: number;
}

const TenantManager: React.FC = () => {
  // State
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);

  // Form state - SIMPLE organization info only
  const [formData, setFormData] = useState({
    organization_name: '',
    admin_email: '',
    billing_email: '',
    allowed_domains: '',
    user_whitelist: '',
  });

  // Load tenants
  const loadTenants = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/tenants');
      setTenants(response.data.tenants || response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  // Handle form input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      organization_name: '',
      admin_email: '',
      billing_email: '',
      allowed_domains: '',
      user_whitelist: '',
    });
    setEditingTenant(null);
  };

  // Open create dialog
  const handleOpenCreate = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  // Open edit dialog
  const handleOpenEdit = (tenant: Tenant) => {
    setEditingTenant(tenant);
    setFormData({
      organization_name: tenant.organization_name,
      admin_email: tenant.admin_email,
      billing_email: tenant.billing_email || '',
      allowed_domains: '',
      user_whitelist: '',
    });
    setCreateDialogOpen(true);
  };

  // Handle tenant creation/update
  const handleSaveTenant = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Prepare data
      const tenantData = {
        organization_name: formData.organization_name,
        admin_email: formData.admin_email,
        billing_email: formData.billing_email || undefined,
        allowed_domains: formData.allowed_domains
          ? formData.allowed_domains.split(',').map(d => d.trim()).filter(Boolean)
          : [],
        user_whitelist: formData.user_whitelist
          ? formData.user_whitelist.split(',').map(e => e.trim()).filter(Boolean)
          : [],
      };

      if (editingTenant) {
        // Update existing tenant
        await api.put(`/tenants/${editingTenant.id}`, tenantData);
        setSuccess('Organization updated successfully');
      } else {
        // Create new tenant
        await api.post('/tenants', tenantData);
        setSuccess('Organization created successfully. You can now create projects for this organization.');
      }

      // Close dialog and reload
      setCreateDialogOpen(false);
      resetForm();
      await loadTenants();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save organization');
    } finally {
      setLoading(false);
    }
  };

  // Handle tenant deletion
  const handleDeleteTenant = async (tenantId: string) => {
    if (!window.confirm('Are you sure you want to delete this organization? This will also delete all associated projects.')) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await api.delete(`/tenants/${tenantId}`);
      setSuccess('Organization deleted successfully');
      await loadTenants();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete organization');
    } finally {
      setLoading(false);
    }
  };

  // Status chip color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'error':
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  // Status icon
  const getStatusIcon = (status: string): React.ReactElement | undefined => {
    switch (status) {
      case 'active':
        return <ActiveIcon fontSize="small" />;
      case 'pending':
        return <PendingIcon fontSize="small" />;
      case 'error':
      case 'suspended':
        return <ErrorIcon fontSize="small" />;
      default:
        return undefined;
    }
  };

  // Validate form
  const isFormValid = () => {
    return (
      formData.organization_name.trim() !== '' &&
      formData.admin_email.trim() !== '' &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)
    );
  };

  return (
    <Box>
      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Organizations are containers for projects. Keep this simple - complex configuration happens at the project level.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadTenants}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Create Organization
          </Button>
        </Box>
      </Box>

      {/* Success Alert */}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tenants Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Organization Name</TableCell>
              <TableCell>Admin Email</TableCell>
              <TableCell>Billing Email</TableCell>
              <TableCell>Projects</TableCell>
              <TableCell>Users</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary">
                    No organizations found. Create your first organization to get started!
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tenants.map(tenant => (
                <TableRow key={tenant.id}>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(tenant.status)}
                      label={tenant.status}
                      color={getStatusColor(tenant.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {tenant.organization_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tenant.tenant_id}
                    </Typography>
                  </TableCell>
                  <TableCell>{tenant.admin_email}</TableCell>
                  <TableCell>{tenant.billing_email || '-'}</TableCell>
                  <TableCell>
                    <Chip label={tenant.project_count || 0} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={tenant.user_count || 0} size="small" />
                  </TableCell>
                  <TableCell>{new Date(tenant.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEdit(tenant)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteTenant(tenant.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Organization Dialog - SIMPLE */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          resetForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingTenant ? 'Edit Organization' : 'Create New Organization'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Organizations are simple containers. All complex configuration (resources, models, components, city data)
              happens when you create a project for this organization.
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organization Name"
                  value={formData.organization_name}
                  onChange={e => handleInputChange('organization_name', e.target.value)}
                  helperText="Official name of the organization or client"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Admin Email"
                  type="email"
                  value={formData.admin_email}
                  onChange={e => handleInputChange('admin_email', e.target.value)}
                  helperText="Primary admin contact for this organization"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Billing Email (Optional)"
                  type="email"
                  value={formData.billing_email}
                  onChange={e => handleInputChange('billing_email', e.target.value)}
                  helperText="Email for billing and invoices (if different from admin)"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Allowed Domains (Optional)"
                  value={formData.allowed_domains}
                  onChange={e => handleInputChange('allowed_domains', e.target.value)}
                  helperText="Comma-separated domains for SSO/email restrictions (e.g., company.com, subsidiary.com)"
                  placeholder="example.com, company.org"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="User Whitelist (Optional)"
                  value={formData.user_whitelist}
                  onChange={e => handleInputChange('user_whitelist', e.target.value)}
                  helperText="Comma-separated email addresses to whitelist specific users"
                  placeholder="user1@email.com, user2@email.com"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTenant}
            disabled={loading || !isFormValid()}
          >
            {loading ? <CircularProgress size={24} /> : (editingTenant ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantManager;
